<template>
  <div
    v-if="showGate"
    class="collection-gate"
    :style="{
      '--backgroundImageSmall': `url(${backgroundImages?.smallMedia})`,
      '--backgroundImageMedium': `url(${backgroundImages?.mediumMedia})`,
      '--backgroundImageLarge': `url(${backgroundImages?.largeMedia})`,
    }"
  >
    <IconButton
      theme="white"
      icon="cross"
      class="close-btn"
      @click="redirectToHomepage"
    />
    <div class="collection-gate__content">
      <div class="collection-gate__background">
        <div class="background-image"></div>
        <div class="overlay"></div>
      </div>
      <div class="collection-gate__mobile-background">
        <Media
          :sources="[
            {
              url: 'https://cdn.shopify.com/videos/c/o/v/e274301948cc44e79329569a884469f0.mp4',
              mimeType: 'video/mp4',
            },
          ]"
          :preview-images="[
            {
              url: 'https://cdn.shopify.com/s/files/1/0942/9062/files/HIBISCUS_DRESS_2_9d3be009-9044-4c4e-b0f0-bb5790ee7e4d.jpg?v=1742296436',
            },
          ]"
          cover
          class="media"
          cover-position="center center"
        />
        <div class="overlay"></div>
      </div>
      <div class="collection-gate__content-inner">
        <CollectionGateOpening
          v-if="showLandingOpening"
          :end-date="endDate"
          @login="handleShowAccountForm('login')"
          @register="handleShowAccountForm('register')"
        />
        <template v-else-if="shownAccountForm">
          <LandingLoginForm
            v-if="shownAccountForm === 'login'"
            :return-url="`/collections/${collectionHandle}`"
            :hide-register-link="true"
            @success="handleSuccess"
            @register="shownAccountForm = 'register'"
          />
        </template>
        <CollectionGateSuccess
          v-else-if="showGateSuccess"
          :end-date="endDate"
        />
      </div>
    </div>
    <div class="collection-gate__images">
      <div class="video-container">
        <Media
          :sources="[
            {
              url: 'https://cdn.shopify.com/videos/c/o/v/e274301948cc44e79329569a884469f0.mp4',
              mimeType: 'video/mp4',
            },
          ]"
          :preview-images="[
            {
              url: 'https://cdn.shopify.com/s/files/1/0942/9062/files/HIBISCUS_DRESS_1_1.jpg?v=1742295763',
            },
          ]"
          cover
          autoplay
          loop
          muted
          playsinline
          class="media"
          cover-position="center center"
        />
      </div>
      <div class="overlay"></div>
    </div>
  </div>
</template>

<script>
import LandingLoginForm from "@/components/landing/LandingLoginForm.vue";
import CollectionGateSuccess from "@/components/collection/CollectionGateSuccess.vue";
import CollectionGateOpening from "@/components/collection/CollectionGateOpening.vue";
import IconButton from "@/components/IconButton.vue";
import Media from "@/components/Media.vue";
import { mapGetters, mapState } from "vuex";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import { collectionGateAccessTimes } from "@/config.js";

export default {
  name: "CollectionGate",
  components: {
    LandingLoginForm,
    CollectionGateSuccess,
    CollectionGateOpening,
    IconButton,
    Media,
  },
  inject: ["mq"],
  props: {
    collectionHandle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      success: false,
      shownAccountForm: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapState("auth", ["customer"]),
    ...mapGetters("app", ["currencyCode"]),
    showGate() {
      console.log("Checking gate conditions...");
      const { collectionGateEnabled = true } = getFeatureToggles();

      // Check if gate is enabled and if this is the correct collection
      if (
        !collectionGateEnabled ||
        this.collectionHandle !== "the-hibiscus-collection"
      ) {
        return false;
      }

      // Check if user has already viewed the collection
      const hasViewedCollection =
        localStorage.getItem(`collection_access_${this.collectionHandle}`) ===
        "true";
      if (hasViewedCollection) {
        return false;
      }

      // Check for all access tag
      const userTags = this.customer?.tags || [];
      if (userTags.includes("allaccess")) {
        // Store that they've viewed the collection
        localStorage.setItem(
          `collection_access_${this.collectionHandle}`,
          "true"
        );
        return false;
      }

      // Get the current currency's access times
      const currencyAccessTimes =
        collectionGateAccessTimes[this.currencyCode.toLowerCase()] ||
        collectionGateAccessTimes.gbp;
      if (!currencyAccessTimes) {
        console.warn("No access times found for currency:", this.currencyCode);
        return false;
      }

      // Get current time in UTC
      const now = new Date();
      const tier2AccessTime = new Date(currencyAccessTimes.level2Plus);
      const tier1AccessTime = new Date(currencyAccessTimes.level1);
      const disabledTime = new Date(currencyAccessTimes.disabled);

      // If we've passed the disabled time, gate is completely disabled
      if (now >= disabledTime) {
        console.log("Gate disabled - past disabled time");
        return false;
      }

      // Check user's tier level
      const tier2Plus = userTags.some((tag) =>
        ["tier: Level 2", "tier: Level 3", "tier: Level 4"].includes(tag)
      );
      const tier1 = userTags.includes("tier: Level 1");

      console.log("Currency:", this.currencyCode);
      console.log("User tier 2+:", tier2Plus);
      console.log("User tier 1:", tier1);
      console.log("Current time (UTC):", now.toISOString());
      console.log("Tier 2+ access time:", tier2AccessTime.toISOString());
      console.log("Tier 1 access time:", tier1AccessTime.toISOString());
      console.log("Gate disabled time:", disabledTime.toISOString());
      console.log(
        "Current time >= Tier 1 access time:",
        now >= tier1AccessTime
      );
      console.log(
        "Current time >= Tier 2+ access time:",
        now >= tier2AccessTime
      );

      // If user is tier 2+ and we've passed their access time
      if (tier2Plus && now >= tier2AccessTime) {
        console.log("Gate disabled - Tier 2+ user past access time");
        return false;
      }

      // If user is tier 1 and we've passed their access time
      if (tier1 && now >= tier1AccessTime) {
        console.log("Gate disabled - Tier 1 user past access time");
        return false;
      }

      // Show the gate for everyone else
      return true;
    },
    endDate() {
      // Get the current currency's access times
      const currencyAccessTimes =
        collectionGateAccessTimes[this.currencyCode.toLowerCase()] ||
        collectionGateAccessTimes.gbp;
      if (!currencyAccessTimes) {
        return null;
      }

      // Check user's tier level
      const userTags = this.customer?.tags || [];
      const tier1 = userTags.includes("tier: Level 1");

      // Return appropriate access time based on user tier
      return tier1
        ? currencyAccessTimes.level1
        : currencyAccessTimes.level2Plus;
    },
    showLandingOpening() {
      if (this.success || this.showGateSuccess) {
        return false;
      }
      return this.shownAccountForm === null;
    },
    showGateSuccess() {
      if (!this.isLoggedIn) {
        return false;
      }

      // Get the current currency's access times
      const currencyAccessTimes =
        collectionGateAccessTimes[this.currencyCode.toLowerCase()] ||
        collectionGateAccessTimes.gbp;
      if (!currencyAccessTimes) {
        return false;
      }

      // Get current time in UTC
      const now = new Date();
      const tier2AccessTime = new Date(currencyAccessTimes.level2Plus);
      const tier1AccessTime = new Date(currencyAccessTimes.level1);
      const disabledTime = new Date(currencyAccessTimes.disabled);

      // Check user's tier level
      const userTags = this.customer?.tags || [];
      const tier2Plus = userTags.some((tag) =>
        ["tier: Level 2", "tier: Level 3", "tier: Level 4"].includes(tag)
      );
      const tier1 = userTags.includes("tier: Level 1");

      // If we've passed the disabled time, don't show success
      if (now >= disabledTime) {
        return false;
      }

      // For Tier 2+ users
      if (tier2Plus) {
        // Show countdown if before their access time
        if (now < tier2AccessTime) {
          return true;
        }
        // Show success message if within their access window
        return now >= tier2AccessTime && now < disabledTime;
      }

      // For Tier 1 users
      if (tier1) {
        // Show countdown if before their access time
        if (now < tier1AccessTime) {
          return true;
        }
        // Show success message if within their access window
        return now >= tier1AccessTime && now < disabledTime;
      }

      // For users with no tier level, show success page with countdown to disabled time
      return now < disabledTime;
    },
    userTierLevel() {
      const userTags = this.customer?.tags || [];
      if (
        userTags.some((tag) =>
          ["tier: Level 2", "tier: Level 3", "tier: Level 4"].includes(tag)
        )
      ) {
        return 2;
      }
      if (userTags.includes("tier: Level 1")) {
        return 1;
      }
      return 0;
    },
    backgroundImages() {
      return {
        smallMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/HIBISCUS_DRESS_1_1.jpg?v=1742295763",
        mediumMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/HIBISCUS_DRESS_1_1.jpg?v=1742295763",
        largeMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/HIBISCUS_DRESS_1_1.jpg?v=1742295763",
      };
    },
  },
  methods: {
    handleShowAccountForm(form) {
      this.shownAccountForm = form;
    },
    handleSuccess() {
      this.success = true;
      this.shownAccountForm = null;

      // Store that user has successfully accessed this collection
      localStorage.setItem(
        `collection_access_${this.collectionHandle}`,
        "true"
      );
    },
    redirectToHomepage() {
      window.location.href = "/";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.collection-gate {
  position: fixed;
  z-index: 1000;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: transparent;
  display: flex;
  flex-direction: column-reverse;
  overflow: hidden;

  @include respond-above(md) {
    flex-direction: row;
    height: auto;
    background-color: transparent;
  }

  &__content,
  &__images {
    flex: 0 0 50%;
  }

  .close-btn {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 20;
    padding: 8px;
    border-radius: 50%;
    background-color: transparent;

    &:deep(.icon) {
      color: $color-white;
    }
  }
}

.collection-gate__content {
  color: $color-white;
  height: 100vh;
  position: relative;
  overflow: visible;
  padding: 0;
  margin: 0;
  background-image: none;
  background-color: transparent;
  flex: 0 0 50%;
  order: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  @include respond-above(md) {
    padding-top: 0;
    background-image: none;
    border-right: none;
  }

  .collection-gate__background {
    display: none;

    @include respond-above(md) {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
    }

    .background-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url("https://cdn.shopify.com/s/files/1/0942/9062/files/HIBISCUS_DRESS_1_1.jpg?v=1742295763");
      background-size: cover;
      background-position: center;
    }

    .overlay {
      position: absolute;
      content: "";
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0.7) 100%
      );
      z-index: 1;
    }
  }

  .collection-gate__mobile-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 0;

    .media {
      height: 100%;
      width: 100%;
      position: relative;
    }

    .overlay {
      position: absolute;
      content: "";
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0.7) 100%
      );
      z-index: 1;
    }

    @include respond-above(md) {
      display: none;
    }
  }

  &::before {
    display: none;
  }
}

.collection-gate__content-inner {
  position: relative;
  z-index: 3;
  text-align: center;
  padding: 20px;
  max-width: 480px;
  width: 100%;
  margin: 0 auto;

  @include respond-below(md) {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 380px;
  }

  @include respond-above(md) {
    padding: 40px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
  }
}

.collection-gate__images {
  display: none;
  background-color: $color-black;
  position: relative;
  overflow: hidden;

  @include respond-above(md) {
    display: block;
    flex: 0 0 50%;
    order: 2;
    height: 100vh;
  }

  .video-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .media {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.15);
    z-index: 5;
  }
}

.collection-gate__video-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;

  &::before {
    position: absolute;
    content: "";
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba($color-black, 0.7);
    z-index: 1;
  }

  .image {
    height: 100%;
    width: 100%;
    object-fit: cover;
    position: relative;
    z-index: 0;
  }

  @include respond-above(md) {
    display: none;
  }
}

.media {
  height: 100%;
  width: 100%;

  &:deep(video) {
    height: 100%;
    width: 100%;
    object-fit: cover;
    object-position: center;
  }
}
</style>
